import { useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { type ListEpmMasterVddsQuery, useListEpmMasterVddsQuery } from '@/common/graphql';
import { getVddTotal, MAX_VDD_ENTRIES, type VddSuggestion } from '@/common/types';
import { AllocationField } from './AllocationField';
import { AllocationPercentageInput } from './AllocationPercentageInput';
import { AllocationSelectInput } from './AllocationSelectInput';
import type { AllocationFieldWithSuggestionProps, SuggestionAppend } from './props';
import { SuggestionHint } from './SuggestionHint';
import { useGetComboboxData } from './useGetComboboxData';

interface VddSuggestionAppend extends SuggestionAppend {
  epmMasterVddId: string;
}

export const AllocationFieldVdd = ({
  control,
  errors,
  fields,
  append,
  remove,
  watched,
  suggestions,
  showSuggestionHint,
  register,
}: AllocationFieldWithSuggestionProps<'vdd', VddSuggestion, VddSuggestionAppend>) => {
  const total = useMemo(() => getVddTotal(watched), [watched]);
  const { options, loading } = useGetComboboxData<ListEpmMasterVddsQuery>(
    useListEpmMasterVddsQuery,
    'listEpmMasterVdds',
  );

  return (
    <AllocationField
      addLabel="Add VDD"
      errors={errors}
      fields={fields}
      formKey="vdd"
      loading={loading}
      maxEntries={MAX_VDD_ENTRIES}
      title="VDD (Value Delivered Department)"
      total={total}
      onAdd={() => append({ epmMasterVddId: '', percentage: 0 })}
      onRemove={remove}
      suggestionHint={
        <SuggestionHint
          show={showSuggestionHint}
          suggestions={suggestions}
          watched={watched}
          isItemDifferent={(w, s) => w.epmMasterVddId !== s.epmMasterVddId}
          renderName={s => s.name}
        />
      }
      renderRowContent={i => {
        const alreadySelected = watched.slice(0, i).map(it => it.epmMasterVddId);
        return (
          <>
            <Controller
              control={control}
              name={`vdd.${i}.epmMasterVddId`}
              render={({ field }) => (
                <AllocationSelectInput
                  autoSelectSingle={true}
                  data={options.filter(it => !alreadySelected.includes(it.value))}
                  disabled={loading}
                  placeholder="VDD (Value Delivered Department)"
                  value={field.value || null}
                  onChange={val => field.onChange(val ?? '')}
                />
              )}
            />
            <AllocationPercentageInput
              registration={register(`vdd.${i}.percentage`, { valueAsNumber: true })}
              disabled={loading}
            />
          </>
        );
      }}
    />
  );
};
